import React from 'react'
import { createPortal } from 'react-dom'

// Dark scrollbar CSS - shared across all modals
const DARK_SCROLLBAR_STYLES = `
  .dark-scrollbar::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  
  .dark-scrollbar::-webkit-scrollbar-track {
    background: var(--color-bg-tertiary, #1a1a1a);
    border-radius: 4px;
  }
  
  .dark-scrollbar::-webkit-scrollbar-thumb {
    background: var(--color-border, #374151);
    border-radius: 4px;
    border: 1px solid var(--color-bg-tertiary, #1a1a1a);
  }
  
  .dark-scrollbar::-webkit-scrollbar-thumb:hover {
    background: var(--color-text-muted, #6b7280);
  }
  
  .dark-scrollbar::-webkit-scrollbar-corner {
    background: var(--color-bg-tertiary, #1a1a1a);
  }
  
  /* Firefox dark scrollbar */
  .dark-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: var(--color-border, #374151) var(--color-bg-tertiary, #1a1a1a);
  }
`

type ModalProps = {
  open: boolean
  title?: string
  onClose: () => void
  children: React.ReactNode
  footer?: React.ReactNode
  maxWidthClassName?: string
  closeOnBackdropClick?: boolean
}

export const Modal: React.FC<ModalProps> = ({
  open,
  title,
  onClose,
  children,
  footer,
  maxWidthClassName = 'max-w-2xl',
  closeOnBackdropClick = false,
}) => {
  if (!open) return null

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (closeOnBackdropClick && e.target === e.currentTarget) {
      onClose()
    }
  }

  const modalContent = (
    <>
      <style>{DARK_SCROLLBAR_STYLES}</style>
      <div
        className="fixed inset-0 flex items-center justify-center z-[10050] p-4"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.72)' }}
        onClick={handleBackdropClick}
        aria-hidden={!open}
      >
        <div
          className={`rounded-lg shadow-xl w-full ${maxWidthClassName} max-h-[90vh] overflow-y-auto dark-scrollbar`}
          style={{
            backgroundColor: 'var(--color-bg-primary)',
            boxShadow: 'var(--shadow-xl)',
          }}
          role="dialog"
          aria-modal="true"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              {title ? (
                <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                  {title}
                </h3>
              ) : <div />}
              <button
                onClick={onClose}
                className="transition-colors"
                style={{ color: 'var(--color-text-muted)' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = 'var(--color-text-secondary)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = 'var(--color-text-muted)'
                }}
                aria-label="Close"
              >
                {/* simple X icon to avoid coupling */}
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
                  <path fillRule="evenodd" d="M5.47 5.47a.75.75 0 011.06 0L12 10.94l5.47-5.47a.75.75 0 111.06 1.06L13.06 12l5.47 5.47a.75.75 0 11-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 01-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 010-1.06z" clipRule="evenodd" />
                </svg>
              </button>
            </div>

            <div>
              {children}
            </div>

            {footer && (
              <div className="flex justify-end space-x-3 pt-4">
                {footer}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )

  return createPortal(modalContent, document.body)
}


